// api.ts — منقول من frontend/src/services/api.js. عنوان الـAPI عبر import.meta.env.VITE_API_URL.
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { localizeServerMessage } from './serverError';
import {
	AUTH_EXPIRED_EVENT,
	clearAuthStorage,
	dispatchWindowEvent,
	readStorage,
	writeStorage,
} from './storage';

interface RefreshResponse {
	token?: string;
	refresh_token?: string;
}

interface ErrorPayload {
	message?: unknown;
	[key: string]: unknown;
}

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const apiBaseURL = import.meta.env.VITE_API_URL || 'http://localhost:3360';

const api = axios.create({
	baseURL: apiBaseURL,
	withCredentials: false,
	timeout: 15_000,
	headers: { Accept: 'application/json' },
});

api.interceptors.request.use((config) => {
	const token = readStorage('token') || readStorage('access_token');
	if (token) config.headers.Authorization = `Bearer ${token}`;
	const roleId = readStorage('role_id');
	if (roleId) config.headers['X-ROLE-ID'] = roleId;
	if (
		!config.headers['Content-Type'] &&
		config.data &&
		typeof config.data === 'object' &&
		!(config.data instanceof FormData)
	) {
		config.headers['Content-Type'] = 'application/json';
	}
	return config;
});

// تجديد صامت single-flight عند 401 — يحلّ انتهاء صلاحيّة الوصول (ساعة واحدة) بلا إخراج المستخدم.
let refreshPromise: Promise<string> | null = null;

function refreshSession(): Promise<string> {
	if (refreshPromise) return refreshPromise;
	const refreshToken = readStorage('refresh_token');
	if (!refreshToken) return Promise.reject(new Error('no_refresh_token'));
	refreshPromise = axios
		.post<RefreshResponse>(`${apiBaseURL}/refresh`, { refresh_token: refreshToken }, { timeout: 15_000 })
		.then(({ data }) => {
			if (!data.token) throw new Error('no_token_in_refresh');
			writeStorage('token', data.token);
			writeStorage('access_token', data.token);
			if (data.refresh_token) writeStorage('refresh_token', data.refresh_token);
			return data.token;
		})
		.finally(() => {
			refreshPromise = null;
		});
	return refreshPromise;
}

function expireSession(): void {
	clearAuthStorage();
	dispatchWindowEvent(AUTH_EXPIRED_EVENT);
	if (!/\/login\/?$/.test(window.location.pathname)) window.location.assign('/login');
}

api.interceptors.response.use(
	(response) => response,
	async (reason: unknown) => {
		const error = reason as AxiosError<ErrorPayload>;
		const original = (error.config || {}) as RetryableConfig;
		const status = error.response?.status;
		const url = String(original.url || '');
		const isAuthEndpoint = /\/(login|refresh)\/?$/.test(url);
		if (status === 401 && !original._retry && !isAuthEndpoint) {
			original._retry = true;
			try {
				const token = await refreshSession();
				original.headers.Authorization = `Bearer ${token}`;
				return api(original);
			} catch {
				expireSession();
			}
		}
		const payload = error.response?.data;
		if (payload && typeof payload.message === 'string') {
			payload.message = localizeServerMessage(payload.message) as string;
		}
		return Promise.reject(error);
	},
);

export default api;
